import {
  AllowedLimits,
  CONTRACT,
  ContractAddress,
  ContractEventParams,
  useAllowance,
  useApproveErc721,
  useContract
} from '@apeiron/contract'
import { CurrencyTokenType, useCurrencyContext } from '@apeiron/currency'
import { PUBLIC_RENTAL_GROUP_ID } from '@apeiron/orbital'
import { PlanetLite } from '@apeiron/planet'
import { Star } from '@apeiron/star'
import {
  CustomError,
  useAccountContext,
  useChainSwitcher,
  useMessageContext,
  useWeb3
} from '@apeiron/temp'
import { ApeironPleaseWait } from '@apeiron/ui'
import { useMktErrorHandling, useWalletAddress } from '@apeiron/wallet'
import ApeironTwoColorMessage from '@src/components/share/apeiron/ApeironTwoColorMessage'
import { ERROR_CODE } from '@src/constants/error'
import { OrbitalTrackLite, OrbitalTrackSetting } from '@src/types/orbitalTrack'
import * as R from 'ramda'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Address } from 'viem'

const extractSuccessIds = R.pipe<any, bigint[], bigint[], string[]>(
  R.propOr([], '_successPlanetArray'),
  R.reject((id: bigint) => id.toString() === '0'),
  R.map(id => id.toString())
)

const useOrbitalTrackAttach = (
  type: CurrencyTokenType = CurrencyTokenType.Unspecified
): Hook => {
  const { t } = useTranslation()

  const { toWei } = useWeb3()

  const { account } = useAccountContext()

  const { walletAddress } = account

  const { setInfo } = useMessageContext()

  const { showErrorPopup } = useMktErrorHandling()

  const { getCurrencyContract, getCurrency } = useCurrencyContext()

  const { getContractAddress } = useChainSwitcher()

  const { compareAddress } = useWalletAddress()

  const approvePlanet = useApproveErc721(
    walletAddress as Address,
    getContractAddress(CONTRACT.PLANET.GET_USER_ALLOWANCE.address)
  )

  const contractSet = getCurrencyContract(type)

  const { request: approveToken } = useAllowance({
    contractTypes: {
      allowance: contractSet.GET_USER_ALLOWANCE,
      request: contractSet.REQUEST_ALLOWANCE
    }
  })

  const { sendWithOption: ownerAttachPlanet } = useContract<void>(
    CONTRACT.STAR.OWNER_ATTACH_PLANET
  )

  const { sendWithOption: ownerDetachPlanet } = useContract<void>(
    CONTRACT.STAR.OWNER_DETACH_PLANET
  )

  const { sendWithOption: renterAttachPlanet } = useContract<void>(
    CONTRACT.STAR.RENTER_ATTACH_PLANET
  )

  const { sendWithOption: renterDetachPlanet } = useContract<void>(
    CONTRACT.STAR.RENTER_DETACH_PLANET
  )

  const { sendWithOption: renewPlanet } = useContract<void>(
    CONTRACT.STAR.RENEW_RENTAL
  )

  const arrayToNumber = useCallback((array: string[]): number[] => {
    return array.map(item => Number(item))
  }, [])

  const attachPlanets = useCallback(
    async (
      star: Star,
      selectedPlanets: PlanetLite[],
      setting: OrbitalTrackSetting | null = null
    ): Promise<string[]> => {
      let returnValues = null

      let attachedIds: string[] = []

      let failReasons: string[] = []

      try {
        setInfo({
          message: t('common.wait_token_approve')
        })

        const orbitAddress = getContractAddress(
          ContractAddress.STAR_ORBITAL_TRACK
        )

        const approved = await approvePlanet(orbitAddress)

        if (!approved) {
          throw new CustomError(
            'cannot approve planet on attach orbital track',
            ERROR_CODE.CONTRACT.APPROVAL_FAIL
          )
        }

        const isOwner = compareAddress(walletAddress, star.owner?.walletAddress)

        if (!isOwner) {
          const currency = getCurrency(setting?.currencyType || type)

          // rentalFee already powed by 10^{decimals}
          const fee =
            BigInt(setting?.rentalFee || '0') *
            BigInt(R.length(selectedPlanets))

          const allowance = currency
            ? BigInt(
                toWei(
                  R.propOr(0, currency?.type, AllowedLimits),
                  currency.decimals || 0
                )
              )
            : BigInt('0')

          const spenderAddress = getContractAddress(
            CONTRACT.STAR.RENTER_ATTACH_PLANET.spenderAddress
          )

          await approveToken(walletAddress, spenderAddress, fee, allowance)
        }

        setInfo({
          message: (
            <ApeironPleaseWait
              subject={t(
                'godiverse.orbital_track.attach_planet.message.attach_now'
              )}
            />
          )
        })

        if (isOwner) {
          const events = await ownerAttachPlanet(
            { verifyEvent: ContractEventParams.OrbitOwnerAttach },
            star.id,
            R.pluck('planetID', selectedPlanets)
          )

          returnValues = R.pathOr(
            null,
            [ContractEventParams.OrbitOwnerAttach.name, 'returnValues'],
            events
          )
        } else {
          const groupId = setting?.inviteGroupId || PUBLIC_RENTAL_GROUP_ID

          const events = await renterAttachPlanet(
            { verifyEvent: ContractEventParams.OrbitRenterAttach },
            star.id,
            R.pluck('planetID', selectedPlanets),
            R.repeat(groupId, R.length(selectedPlanets))
          )

          returnValues = R.pathOr(
            null,
            [ContractEventParams.OrbitRenterAttach.name, 'returnValues'],
            events
          )
        }

        setInfo(null)

        attachedIds = extractSuccessIds(returnValues)
        failReasons = R.propOr([], '_failReasonArray', returnValues)

        if (R.isEmpty(attachedIds) && !R.isEmpty(failReasons)) {
          throw new CustomError(
            R.head(failReasons) as string,
            ERROR_CODE.RENTAL.FAIL_TO_ATTACH
          )
        }
      } catch (error) {
        // fail and don't display fail count
        showErrorPopup(error)

        return attachedIds
      }

      const successCount = R.length(attachedIds)

      const failCount = R.length(selectedPlanets) - R.length(attachedIds)

      if (failCount > 0) {
        // partial fail
        setInfo({
          confirm: t('common.confirm'),
          title: (
            <ApeironTwoColorMessage
              message={t('godiverse.orbital_track.attach_result', {
                success: successCount,
                fail: failCount
              })}
            />
          )
        })
      }

      return attachedIds
    },
    [
      setInfo,
      t,
      getContractAddress,
      approvePlanet,
      compareAddress,
      walletAddress,
      getCurrency,
      type,
      toWei,
      approveToken,
      ownerAttachPlanet,
      renterAttachPlanet,
      showErrorPopup
    ]
  )

  const detachPlanets = useCallback(
    async (tracks: OrbitalTrackLite[]): Promise<string[]> => {
      let detachedIds: string[] = []

      try {
        setInfo({
          message: t('common.wait_token_approve')
        })

        const orbitCallAddress = getContractAddress(
          ContractAddress.STAR_ORBITAL_TRACK_CALLER
        )

        await approvePlanet(orbitCallAddress)

        setInfo({
          message: (
            <ApeironPleaseWait
              subject={t(
                'godiverse.orbital_track.attach_planet.message.detach_now'
              )}
            />
          )
        })

        const groupedByRole = R.groupBy(track => {
          return compareAddress(track.star?.owner?.walletAddress, walletAddress)
            ? 'owner'
            : 'renter'
        }, tracks)

        const ownerTracks = R.propOr(
          [],
          'owner',
          groupedByRole
        ) as OrbitalTrackLite[]

        const renterTracks = R.propOr(
          [],
          'renter',
          groupedByRole
        ) as OrbitalTrackLite[]

        if (!R.isEmpty(ownerTracks)) {
          const events = await ownerDetachPlanet(
            { verifyEvent: ContractEventParams.OrbitOwnerDetach },
            arrayToNumber(R.pluck('starId', ownerTracks)),
            arrayToNumber(R.pluck('planetId', ownerTracks))
          )

          const returnValues = R.pathOr(
            null,
            [ContractEventParams.OrbitOwnerDetach.name, 'returnValues'],
            events
          )

          detachedIds = R.concat(extractSuccessIds(returnValues), detachedIds)
        }

        if (!R.isEmpty(renterTracks)) {
          const events = await renterDetachPlanet(
            { verifyEvent: ContractEventParams.OrbitRenterDetach },
            arrayToNumber(R.pluck('starId', renterTracks)),
            arrayToNumber(R.pluck('planetId', renterTracks))
          )

          const returnValues = R.pathOr(
            null,
            [ContractEventParams.OrbitRenterDetach.name, 'returnValues'],
            events
          )

          detachedIds = R.concat(extractSuccessIds(returnValues), detachedIds)
        }

        setInfo(null)
      } catch (error) {
        showErrorPopup(error)

        return detachedIds
      }

      const successCount = R.length(detachedIds)

      const failCount = R.length(tracks) - R.length(detachedIds)

      if (failCount > 0) {
        // partial fail
        setInfo({
          confirm: t('common.confirm'),
          title: (
            <ApeironTwoColorMessage
              message={t('godiverse.orbital_track.detach_result', {
                success: successCount,
                fail: failCount
              })}
            />
          )
        })
      }

      return detachedIds
    },
    [
      setInfo,
      t,
      getContractAddress,
      approvePlanet,
      compareAddress,
      walletAddress,
      ownerDetachPlanet,
      arrayToNumber,
      renterDetachPlanet,
      showErrorPopup
    ]
  )

  const renewPlanets = useCallback(
    async (tracks: OrbitalTrackLite[]): Promise<string[]> => {
      let renewedIds: string[] = []

      try {
        setInfo({
          message: t('common.wait_token_approve')
        })

        const orbitCallerAddress = getContractAddress(
          ContractAddress.STAR_ORBITAL_TRACK_CALLER
        )

        await approvePlanet(orbitCallerAddress)

        setInfo({
          message: (
            <ApeironPleaseWait
              subject={t(
                'godiverse.orbital_track.attach_planet.message.renew_now'
              )}
            />
          )
        })

        if (!R.isEmpty(tracks)) {
          const events = await renewPlanet(
            { verifyEvent: ContractEventParams.OrbitRenterRenew },
            arrayToNumber(R.pluck('starId', tracks)),
            arrayToNumber(R.pluck('planetId', tracks))
          )

          const returnValues = R.pathOr(
            null,
            [ContractEventParams.OrbitRenterRenew.name, 'returnValues'],
            events
          )

          renewedIds = R.concat(extractSuccessIds(returnValues), renewedIds)
        }

        setInfo(null)
      } catch (error) {
        showErrorPopup(error)

        return renewedIds
      }

      const successCount = R.length(renewedIds)

      const failCount = R.length(tracks) - R.length(renewedIds)

      if (failCount > 0) {
        // partial fail
        setInfo({
          confirm: t('common.confirm'),
          title: (
            <ApeironTwoColorMessage
              message={t('godiverse.orbital_track.renew_result', {
                success: successCount,
                fail: failCount
              })}
            />
          )
        })
      }

      return renewedIds
    },
    [
      setInfo,
      t,
      getContractAddress,
      approvePlanet,
      renewPlanet,
      arrayToNumber,
      showErrorPopup
    ]
  )

  return { attachPlanets, detachPlanets, renewPlanets }
}

type Hook = {
  attachPlanets: (
    star: Star,
    selectedPlanets: PlanetLite[],
    setting?: OrbitalTrackSetting | null
  ) => Promise<string[]>
  detachPlanets: (tracks: OrbitalTrackLite[]) => Promise<string[]>
  renewPlanets: (tracks: OrbitalTrackLite[]) => Promise<string[]>
}

export default useOrbitalTrackAttach
